import { Grid } from '@mui/material';
import { AVOID_FEATURES, TrafficOptionsDTO } from '@nx-smartmonkey/shared/domain';
import { RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';

import { SHIPPED_HAZARDOUS_GOODS, TRAFFIC_MULTIPLIER_VALUES, TRANSPORT_MODE } from '../../../constants/TrafficOptions';
import { Project } from '../../../domain/project/Project';
import { useRetrieveActiveProjectResources } from '../../../hooks/project/useRetrieveActiveProjectResources';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';

type TrafficOptionsComponentComponent = {
  project: Project;
  asUser?: string;
  onChange?: (...args: any[]) => any;
  handleEnter?: (...args: any[]) => any;
  trafficOptions?: TrafficOptionsDTO;
};

export const TrafficOptionsComponent = ({
  project,
  asUser,
  onChange,
  handleEnter,
  trafficOptions,
}: TrafficOptionsComponentComponent) => {
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { data: userCredentials } = useRetrieveUserCredentials();

  const getMultiplierHumanValue = (value?: number) => {
    if (value) {
      const regularValue = TRAFFIC_MULTIPLIER_VALUES.find((val) => val.value === value);
      if (regularValue) {
        return <Text style={{ width: `100%`, textAlign: `start` }} intlId={regularValue.labelId} />;
      }
      return <Text style={{ width: `100%`, textAlign: `start` }}>{value}</Text>;
    }
    return <Text style={{ width: `100%`, textAlign: `start` }} intlId="not_selected" />;
  };

  const onUpdate = (data: any) => {
    if (onChange) {
      onChange(data);
    } else if (handleEnter) {
      handleEnter(data);
    }
  };

  if (!project) return null;

  return (
    <>
      {userCredentials?.isStaff(asUser) ? (
        <>
          <Grid
            item
            key="traffic_options-multiplier"
            style={{
              border: `1px solid ${RoutalPalette.warning.medium}`,
              padding: `8px`,
              borderRadius: `2px`,
            }}
          >
            <Text key="traffic_options.details.multiplier" intlId="traffic_options.details.multiplier" />

            <VSpacer small />

            <InputText
              key="number-truck-multiplier"
              inputContainerStyles={{
                width: `100%`,
              }}
              variant="outlined"
              type="number"
              fieldValue={trafficOptions?.multiplier}
              {...(handleEnter
                ? {
                    handleEnter: (value: any) => {
                      onUpdate({
                        ...trafficOptions,
                        multiplier: value,
                      });
                    },
                  }
                : {})}
              {...(onChange
                ? {
                    onChange: (value: any) => {
                      onUpdate({
                        ...trafficOptions,
                        multiplier: value,
                      });
                    },
                  }
                : {})}
            />

            <VSpacer small />
          </Grid>
          <VSpacer medium />
        </>
      ) : null}

      {!projectResources?.traffic ? (
        <Grid item key="traffic-options-multiplier">
          <Text key="traffic_options.details.multiplier" intlId="traffic_options.details.multiplier" />

          <VSpacer small />

          <RoutalSelect
            id="select-multiplier"
            popoverProps={{
              options: TRAFFIC_MULTIPLIER_VALUES.map(
                (option: any): RoutalSelectMultiSelectOption => ({
                  key: `multiplier-menuItem${option.value}`,
                  content: <Text intlId={`${option.labelId}`} style={{ cursor: `pointer` }} />,
                  onClick: () => {
                    onUpdate({
                      ...trafficOptions,
                      multiplier: option.value,
                    });
                  },
                })
              ),
            }}
          >
            <Text style={{ width: `100%`, textAlign: `start` }}>
              {getMultiplierHumanValue(trafficOptions?.multiplier)}
            </Text>
          </RoutalSelect>
          <VSpacer medium />
        </Grid>
      ) : (
        <>
          <Grid item key="traffic-options-avoid-features">
            <Text key="traffic_options.details.avoid.features" intlId="traffic_options.details.avoid.features" />

            <VSpacer small />

            <RoutalSelect
              id="select-avoid"
              popoverProps={{
                options: [
                  ...AVOID_FEATURES.map(
                    (option): RoutalSelectMultiSelectOption => ({
                      key: `avoid-menuItem${option}`,
                      checked: (trafficOptions?.avoid?.features ?? []).includes(option),
                      hideCheckIcon: false,
                      closeOnClick: false,
                      content: <Text intlId={`${option}`} style={{ cursor: `pointer` }} />,
                      onClick: (e: any, value: any) => {
                        let avoidFeatures = [];

                        if ((trafficOptions?.avoid?.features ?? []).includes(option)) {
                          avoidFeatures = (trafficOptions?.avoid?.features ?? []).filter(
                            (key: string) => key !== option
                          );
                        } else {
                          avoidFeatures = (trafficOptions?.avoid?.features ?? []).concat(option);
                        }

                        onUpdate({
                          ...trafficOptions,
                          avoid: {
                            features: avoidFeatures,
                          },
                        });
                      },
                    })
                  ),
                ],
              }}
            >
              <Text
                style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}
                intlId="traffic_options.details.avoid.features.num_selected"
                intlValues={{ num: (trafficOptions?.avoid?.features ?? []).length }}
              />
            </RoutalSelect>

            <VSpacer medium />
          </Grid>

          <Grid item key="traffic-options-mode">
            <Text key="traffic_options.details.mode" intlId="traffic_options.details.mode" />

            <VSpacer small />

            <RoutalSelect
              id="select-mode"
              popoverProps={{
                options: [
                  ...TRANSPORT_MODE.map(
                    (option): RoutalSelectMultiSelectOption => ({
                      key: `mode-menuItem${option.value}`,
                      content: <Text intlId={`${option.labelId}`} style={{ cursor: `pointer` }} />,
                      onClick: () => {
                        onUpdate({
                          ...trafficOptions,
                          mode: option.value,
                          ...(option.value !== `truck` ? { truck: undefined } : {}),
                        });
                      },
                    })
                  ),
                ],
              }}
            >
              <Text
                style={{ width: `100%`, textAlign: `start`, cursor: `pointer` }}
                intlId={`traffic_options.details.mode.${trafficOptions?.mode ?? `car`}`}
              />
            </RoutalSelect>
            <VSpacer medium />
          </Grid>

          {trafficOptions?.mode === `truck` ? (
            <>
              <Grid item key="traffic-options-shipped_hazardous_goods">
                <Text
                  key="traffic_options.details.truck.shipped_hazardous_goods"
                  intlId="traffic_options.details.truck.shipped_hazardous_goods"
                />

                <VSpacer small />

                <RoutalSelect
                  id="select-shipped_hazardous_goods"
                  popoverProps={{
                    options: [
                      ...SHIPPED_HAZARDOUS_GOODS.map(
                        (option): RoutalSelectMultiSelectOption => ({
                          key: `shipped_hazardous_goods-menuItem${option.value}`,
                          checked: trafficOptions?.truck?.shipped_hazardous_goods?.includes(option.value),
                          hideCheckIcon: false,
                          closeOnClick: false,
                          content: <Text intlId={`${option.labelId}`} style={{ cursor: `pointer` }} />,
                          onClick: (e: any, value: any) => {
                            let goods = [];

                            if ((trafficOptions?.truck?.shipped_hazardous_goods ?? []).includes(option.value)) {
                              goods = (trafficOptions?.truck?.shipped_hazardous_goods ?? []).filter(
                                (key: string) => key !== option.value
                              );
                            } else {
                              goods = (trafficOptions?.truck?.shipped_hazardous_goods ?? []).concat(option.value);
                            }

                            onUpdate({
                              ...trafficOptions,
                              truck: {
                                ...(trafficOptions.truck ?? {}),
                                shipped_hazardous_goods: goods.length === 0 ? null : goods,
                              },
                            });
                          },
                        })
                      ),
                    ],
                  }}
                >
                  <Text
                    style={{ width: `100%`, textAlign: `start` }}
                    intlId="traffic_options.details.truck.shipped_hazardous_goods.num_selected"
                    intlValues={{ num: (trafficOptions?.truck?.shipped_hazardous_goods ?? []).length }}
                  />
                </RoutalSelect>
                <VSpacer medium />
              </Grid>

              <Grid item key="traffic_options-truck-gross_weight">
                <Text
                  key="traffic_options.details.truck.gross_weight"
                  intlId="traffic_options.details.truck.gross_weight"
                />

                <VSpacer small />

                <InputText
                  key="number-truck-gross_weight"
                  inputContainerStyles={{
                    width: `100%`,
                  }}
                  variant="outlined"
                  type="number"
                  fieldValue={trafficOptions?.truck?.gross_weight}
                  {...(handleEnter
                    ? {
                        handleEnter: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              gross_weight: value,
                            },
                          });
                        },
                      }
                    : {})}
                  {...(onChange
                    ? {
                        onChange: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              gross_weight: value,
                            },
                          });
                        },
                      }
                    : {})}
                  suffix="kg"
                />
                <VSpacer medium />
              </Grid>

              <Grid item key="traffic_options-truck-height">
                <Text key="traffic_options.details.truck.height" intlId="traffic_options.details.truck.height" />

                <VSpacer small />

                <InputText
                  key="number-truck-height"
                  inputContainerStyles={{
                    width: `100%`,
                  }}
                  variant="outlined"
                  type="number"
                  fieldValue={trafficOptions?.truck?.height}
                  {...(handleEnter
                    ? {
                        handleEnter: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              height: value,
                            },
                          });
                        },
                      }
                    : {})}
                  {...(onChange
                    ? {
                        onChange: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              height: value,
                            },
                          });
                        },
                      }
                    : {})}
                  suffix="m"
                />
                <VSpacer medium />
              </Grid>

              <Grid item key="traffic_options-truck-width">
                <Text key="traffic_options.details.truck.width" intlId="traffic_options.details.truck.width" />

                <VSpacer small />

                <InputText
                  key="number-truck-width"
                  inputContainerStyles={{
                    width: `100%`,
                  }}
                  variant="outlined"
                  type="number"
                  fieldValue={trafficOptions?.truck?.width}
                  {...(handleEnter
                    ? {
                        handleEnter: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              width: value,
                            },
                          });
                        },
                      }
                    : {})}
                  {...(onChange
                    ? {
                        onChange: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              width: value,
                            },
                          });
                        },
                      }
                    : {})}
                  suffix="m"
                />
                <VSpacer medium />
              </Grid>

              <Grid item key="traffic_options-truck-length">
                <Text key="traffic_options.details.truck.length" intlId="traffic_options.details.truck.length" />

                <VSpacer small />

                <InputText
                  key="number-truck-length"
                  inputContainerStyles={{
                    width: `100%`,
                  }}
                  variant="outlined"
                  type="number"
                  fieldValue={trafficOptions?.truck?.length}
                  {...(handleEnter
                    ? {
                        handleEnter: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              length: value,
                            },
                          });
                        },
                      }
                    : {})}
                  {...(onChange
                    ? {
                        onChange: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              length: value,
                            },
                          });
                        },
                      }
                    : {})}
                  suffix="m"
                />
                <VSpacer medium />
              </Grid>

              {/* tunnel_category */}
              <Grid item key="traffic-options-tunnel_category">
                <Text
                  key="traffic_options.details.truck.tunnel_category"
                  intlId="traffic_options.details.truck.tunnel_category"
                />

                <VSpacer small />

                <RoutalSelect
                  id="select-tunnel_category"
                  popoverProps={{
                    options: [
                      ...[`B`, `C`, `D`, `E`].map(
                        (option): RoutalSelectMultiSelectOption => ({
                          key: `tunnel_category-menuItem${option}`,
                          content: <Text style={{ cursor: `pointer` }}>{option}</Text>,
                          onClick: () => {
                            onUpdate({
                              ...trafficOptions,
                              truck: {
                                ...(trafficOptions.truck ?? {}),
                                tunnel_category: option,
                              },
                            });
                          },
                        })
                      ),
                    ],
                  }}
                >
                  {trafficOptions?.truck?.tunnel_category ? (
                    <Text style={{ width: `100%`, textAlign: `start` }}>{trafficOptions?.truck?.tunnel_category}</Text>
                  ) : (
                    <Text style={{ width: `100%`, textAlign: `start` }} intlId="not_selected" />
                  )}
                </RoutalSelect>
                <VSpacer medium />
              </Grid>

              <Grid item key="traffic_options-truck-axle_count">
                <Text
                  key="traffic_options.details.truck.axle_count"
                  intlId="traffic_options.details.truck.axle_count"
                />

                <VSpacer small />

                <InputText
                  key="number-truck-axle_count"
                  inputContainerStyles={{
                    width: `100%`,
                  }}
                  variant="outlined"
                  type="number"
                  fieldValue={trafficOptions?.truck?.axle_count}
                  {...(handleEnter
                    ? {
                        handleEnter: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              axle_count: value,
                            },
                          });
                        },
                      }
                    : {})}
                  {...(onChange
                    ? {
                        onChange: (value: any) => {
                          onUpdate({
                            ...trafficOptions,
                            truck: {
                              ...(trafficOptions.truck ?? {}),
                              axle_count: value,
                            },
                          });
                        },
                      }
                    : {})}
                  placeholderIntlId="traffic_options.details.truck.axle_count_ph"
                />
                <VSpacer medium />
              </Grid>
            </>
          ) : null}
        </>
      )}
    </>
  );
};
